import React, { createContext, useState, useContext, useCallback, ReactNode, useMemo } from 'react';

// Define la forma del contexto
interface VerificationContextType {
  isVerifying: (transactionId: string) => boolean;
  startVerification: (transactionId: string) => boolean; // Devuelve true si se pudo iniciar, false si ya estaba
  endVerification: (transactionId: string) => void;
}

// Crea el contexto con un valor inicial undefined
const VerificationContext = createContext<VerificationContextType | undefined>(undefined);

// Hook personalizado para usar el contexto fácilmente
export const useVerification = (): VerificationContextType => {
  const context = useContext(VerificationContext);
  if (!context) {
    throw new Error('useVerification debe ser usado dentro de un VerificationProvider');
  }
  return context;
};

// Props para el proveedor
interface VerificationProviderProps {
  children: ReactNode;
}

// Componente Proveedor
export const VerificationProvider: React.FC<VerificationProviderProps> = ({ children }) => {
  // Usamos un Set para almacenar los IDs de transacción que se están verificando
  const [verifyingIds, setVerifyingIds] = useState<Set<string>>(new Set());

  // Función para verificar si un ID está en proceso
  const isVerifying = useCallback((transactionId: string): boolean => {
    return verifyingIds.has(transactionId);
  }, [verifyingIds]);

  // Función para iniciar la verificación (añade ID al Set)
  // Devuelve true si el ID no estaba y se añadió, false si ya existía
  const startVerification = useCallback((transactionId: string): boolean => {
    if (verifyingIds.has(transactionId)) {
      console.warn(`[VerificationContext] Intento de iniciar verificación para ${transactionId} que ya está en curso.`);
      return false; // Ya estaba verificando
    }
    setVerifyingIds(prevIds => {
        const newIds = new Set(prevIds);
        newIds.add(transactionId);
        // Log para ver qué IDs se están verificando
        console.log(`[VerificationContext] Iniciando verificación para ${transactionId}. Verificando ahora:`, Array.from(newIds));
        return newIds;
    });
    return true; // Se inició la verificación
  }, [verifyingIds]); // Dependencia del Set

  // Función para finalizar la verificación (quita ID del Set)
  const endVerification = useCallback((transactionId: string): void => {
    setVerifyingIds(prevIds => {
      const newIds = new Set(prevIds);
      const deleted = newIds.delete(transactionId);
      // Solo loguear si realmente se eliminó algo
      if (deleted) {
          console.log(`[VerificationContext] Finalizando verificación para ${transactionId}. Verificando ahora:`, Array.from(newIds));
      }
      return newIds;
    });
  }, []); // Sin dependencias

  // Memoizar el valor del contexto para evitar re-renders innecesarios
  const contextValue = useMemo(() => ({
    isVerifying,
    startVerification,
    endVerification
  }), [isVerifying, startVerification, endVerification]);

  return (
    <VerificationContext.Provider value={contextValue}>
      {children}
    </VerificationContext.Provider>
  );
};